import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Calendar,
  ExternalLink,
  Clock,
  Loader2,
  AlertTriangle,
  ChevronRight,
  Sparkles,
  Search,
  Globe,
  Tag,
  Info,
  HelpCircle,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "../ui/dialog";
import { API_BASE_URL } from '../../config';
import moment from 'moment';
import { cn } from "../../lib/utils";
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useProjects } from '../../contexts/ProjectContext';

interface SteamEvent {
  id: string;
  name: string;
  url: string;
  startDate: string;
  endDate: string;
  isConfirmed: boolean;
  type: string;
  tags: string[];
  eligibilityText?: string;
}

export default function SteamEventsBox() {
  const { user } = useGlobalState();
  const { activeProject } = useProjects();
  const [events, setEvents] = useState<SteamEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const token = localStorage.getItem('token');

  const isFeatureEnabled = user?.isAdmin || (user?.featureFlags?.steam_roadmap_opportunities ?? true);

  useEffect(() => {
    if (isFeatureEnabled) {
      fetchEvents();
    }
  }, [isFeatureEnabled]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/social/steam-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (err: any) {
      console.error('Error fetching Steam events:', err);
      setError('Failed to load Steam events.');
    } finally {
      setLoading(false);
    }
  };

  const gameTags = useMemo(() => {
    return (Array.isArray(activeProject?.tags) ? activeProject?.tags : []) as string[];
  }, [activeProject]);

  const isMatch = (event: SteamEvent) => {
    const lowerName = event.name.toLowerCase();
    if (event.type === 'FEST' || lowerName.includes('next fest')) return true;

    // Normalization helper
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedGameTags = gameTags.map(normalize);
    const normalizedEventName = normalize(event.name);
    const normalizedEventTags = (event.tags || []).map(normalize);
    const eventNameWords = event.name.toLowerCase()
      .split(/[\s_-]+/)
      .map(normalize)
      .filter(w => w.length >= 3 && w !== 'fest' && w !== 'sale' && w !== 'festival');

    return normalizedGameTags.some(gt =>
      normalizedEventName.includes(gt) ||
      normalizedEventTags.some(et => et.includes(gt) || gt.includes(et)) ||
      eventNameWords.some(word => gt.includes(word) || word.includes(gt))
    );
  };

  const matchingEvents = useMemo(() => {
    return events.filter(isMatch);
  }, [events, gameTags]);

  // Group events by month for the full calendar
  const groupedEvents = useMemo(() => {
    const groups: { [key: string]: SteamEvent[] } = {};
    events.forEach(event => {
      const month = moment(event.startDate).format('MMMM YYYY');
      if (!groups[month]) groups[month] = [];
      groups[month].push(event);
    });
    return Object.entries(groups);
  }, [events]);

  if (!isFeatureEnabled) return null;

  if (loading) {
    return (
      <Card className="bg-card border-border shadow-xl overflow-hidden mt-6">
        <CardHeader className="pb-3 border-b border-border/50">
          <CardTitle className="text-sm font-black text-muted-foreground uppercase tracking-[0.2em] flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            Steam Events Calendar
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 className="h-6 w-6 animate-spin text-primary/50" />
          <p className="text-muted-foreground text-[10px] font-bold uppercase tracking-widest">Updating Calendar...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-card border-border shadow-xl overflow-hidden mt-6">
        <CardContent className="flex flex-col items-center justify-center py-8 space-y-3">
          <AlertTriangle className="h-6 w-6 text-red-500 opacity-50" />
          <p className="text-muted-foreground text-xs font-medium">{error}</p>
          <button onClick={fetchEvents} className="text-[10px] font-black uppercase text-primary hover:text-primary/80 transition-colors">
            Try again
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card border-border shadow-2xl overflow-hidden mt-6">
        <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
              <div className="p-1.5 bg-primary/20 rounded-lg shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              Steam Fest Chances
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-muted-foreground cursor-help transition-colors ml-1" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[280px] bg-muted border-border text-foreground text-[11px] leading-relaxed p-3 shadow-2xl font-normal normal-case tracking-normal">
                    <p>Detected through analysis of your game and tags indicated in the projects list. Add more tags to find more opportunities. Festivals may be subject to eligibility.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-primary/5 text-primary/70 border-primary/10 text-[9px] font-black">
                {matchingEvents.length} MATCHES
              </Badge>
              <button
                onClick={() => setShowFullCalendar(true)}
                className="text-[10px] font-black text-primary hover:text-primary/80 flex items-center gap-1 uppercase tracking-widest"
              >
                View all calendar <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div 
            className="h-[400px] overflow-y-auto"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#475569 rgba(15, 23, 42, 0.5)'
            }}
          >
            <div className="p-4 space-y-4">{matchingEvents.length === 0 ? (
                <div className="py-12 text-center space-y-3">
                  <p className="text-muted-foreground text-xs italic font-medium tracking-wide">No specific matches for your tags right now.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-[10px] border-border text-muted-foreground"
                    onClick={() => setShowFullCalendar(true)}
                  >
                    Explore all events
                  </Button>
                </div>
              ) : (
                matchingEvents.map((event, idx) => {
                  const now = moment();
                  const start = moment(event.startDate);
                  const end = moment(event.endDate);
                  const isHappeningNow = now.isBetween(start, end);
                  const startsInDays = start.diff(now, 'days');
                  const daysLeft = end.diff(now, 'days');
                  const isUpcoming = !isHappeningNow && startsInDays >= 0 && startsInDays <= 7;

                  return (
                    <div
                      key={idx}
                      onClick={() => window.open(event.url, '_blank')}
                      className={cn(
                        "group flex items-center justify-between p-3 rounded-xl cursor-pointer relative overflow-hidden",
                        isHappeningNow
                          ? "bg-primary/10 border border-primary/30 shadow-[0_0_20px_hsl(var(--primary)/0.1)] transition-all duration-300"
                          : isUpcoming
                          ? "bg-amber-500/5 border border-amber-500/30 animate-glow-amber"
                          : "bg-muted/40 border border-transparent hover:border-border hover:bg-card/60 transition-all duration-300"
                      )}
                    >
                      {/* Active Glow Effect */}
                      {isHappeningNow && (
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-transparent animate-pulse" />
                      )}
                      {/* Upcoming Glow Effect - uses same 7s animation as the border */}
                      {isUpcoming && (
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/8 via-transparent to-transparent animate-glow-amber opacity-50" />
                      )}

                      <div className="flex items-center gap-4 flex-1 min-w-0 relative z-10">
                        {/* Date Badge */}
                        <div className={cn(
                          "flex flex-col items-center justify-center min-w-[45px] h-11 rounded-lg border transition-colors",
                          isHappeningNow
                            ? "bg-primary border-primary text-white"
                            : isUpcoming
                            ? "bg-amber-500 border-amber-400 text-white"
                            : "bg-background border-border text-muted-foreground group-hover:border-border"
                        )}>
                          <span className="text-[10px] font-black uppercase leading-none">{start.format('MMM')}</span>
                          <span className="text-sm font-black">{start.format('D')}</span>
                        </div>

                        {/* Event Title & Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className={cn(
                              "font-bold text-sm truncate transition-colors",
                              isHappeningNow ? "text-primary dark:text-primary" : isUpcoming ? "text-amber-700 dark:text-amber-100" : "text-foreground group-hover:text-foreground"
                            )}>
                              {event.name}
                            </h4>
                            {isHappeningNow && (
                              <Sparkles className="h-3 w-3 text-primary animate-pulse shrink-0" />
                            )}
                            {isUpcoming && (
                              <Sparkles className="h-3 w-3 text-amber-400 animate-glow-amber shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <p className="text-[10px] font-medium text-muted-foreground flex items-center gap-1.5 uppercase tracking-wider mt-0.5">
                              <Clock className="h-2.5 w-2.5" />
                              {start.format('D MMM')} — {end.format('D MMM')}
                            </p>
                            {event.tags && event.tags.length > 0 && (
                              <div className="flex items-center gap-1">
                                {event.tags.slice(0, 2).map(tag => (
                                  <Badge key={tag} className="h-3 px-1 text-[7px] bg-primary/10 text-primary border-none font-black uppercase">{tag}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action / Days Left Badge */}
                      <div className="flex items-center gap-3 relative z-10 pl-4">
                        {(isHappeningNow || startsInDays >= 0) && (
                          <div className={cn(
                            "flex flex-col items-center justify-center min-w-[60px] h-14 rounded-lg border px-2",
                            isHappeningNow ? "bg-primary/20 border-primary/40"
                              : isUpcoming ? "bg-amber-500/20 border-amber-500/40"
                              : "bg-background border-border"
                          )}>
                            <span className={cn(
                              "text-xl font-black leading-none",
                              isHappeningNow ? "text-primary" : isUpcoming ? "text-amber-400" : "text-foreground"
                            )}>
                              {isHappeningNow ? (daysLeft === 0 ? '!' : daysLeft) : (startsInDays === 0 ? '!' : startsInDays)}
                            </span>
                            <span className={cn(
                              "text-[9px] font-black uppercase tracking-tighter",
                              isUpcoming ? "text-amber-500/70" : "text-muted-foreground"
                            )}>
                              {isHappeningNow ? (daysLeft === 0 ? 'LAST DAY' : 'DAYS LEFT') : (startsInDays === 0 ? 'STARTS TODAY' : 'DAYS LEFT')}
                            </span>
                          </div>
                        )}
                        {!isHappeningNow && startsInDays < 0 && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-muted-foreground transition-transform group-hover:translate-x-1" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Full Calendar Modal */}
      <Dialog open={showFullCalendar} onOpenChange={setShowFullCalendar}>
        <DialogContent className="bg-card border-border text-foreground sm:max-w-[700px] max-h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="text-2xl font-black flex items-center gap-2">
              <Calendar className="h-6 w-6 text-primary" />
              Steam Fest Chances
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-muted-foreground cursor-help transition-colors ml-1" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[280px] bg-muted border-border text-foreground text-[11px] leading-relaxed p-3 shadow-2xl font-normal normal-case tracking-normal">
                    <p>Detected through analysis of your game and tags indicated in the projects list. Add more tags to find more opportunities. Festivals may be subject to eligibility.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Full list of upcoming Steam sales and festivals.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 px-6" scrollHideDelay={0}>
            <div className="py-4 space-y-8">
              {groupedEvents.map(([month, monthEvents]) => (
                <div key={month} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-[11px] font-black uppercase tracking-[0.3em] text-primary/60 whitespace-nowrap">
                      {month}
                    </h3>
                    <div className="h-px w-full bg-gradient-to-r from-primary/20 to-transparent" />
                  </div>

                  <div className="grid gap-2">
                    {monthEvents.map((event, idx) => {
                      const now = moment();
                      const start = moment(event.startDate);
                      const end = moment(event.endDate);
                      const matched = isMatch(event);
                      const isHappeningNow = now.isBetween(start, end);
                      const daysLeft = end.diff(now, 'days');
                      const calStartsInDays = start.diff(now, 'days');
                      const calIsUpcoming = !isHappeningNow && calStartsInDays >= 0 && calStartsInDays <= 7 && matched;

                      return (
                        <div
                          key={idx}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-xl border relative overflow-hidden",
                            calIsUpcoming
                              ? "bg-amber-500/5 border-amber-500/30 animate-glow-amber"
                              : matched
                              ? "bg-primary/5 border-primary/30 shadow-[0_0_15px_hsl(var(--primary)/0.05)] transition-all"
                              : "bg-muted/40 border-border/50 transition-all"
                          )}
                        >
                          {calIsUpcoming && (
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/8 via-transparent to-transparent animate-glow-amber opacity-50" />
                          )}
                          <div className="flex items-center gap-4">
                            <div className={cn(
                              "flex flex-col items-center justify-center min-w-[40px] h-10 rounded-lg border",
                              matched ? "bg-primary border-primary text-white" : "bg-background border-border text-muted-foreground"
                            )}>
                              <span className="text-[9px] font-black uppercase leading-none">{start.format('MMM')}</span>
                              <span className="text-xs font-black">{start.format('D')}</span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className={cn("font-bold text-sm", matched ? "text-foreground" : "text-muted-foreground")}>
                                  {event.name}
                                </h4>
                                {matched && <Sparkles className="h-3 w-3 text-primary" />}
                                {isHappeningNow && (
                                  <Badge className="bg-primary text-white text-[7px] font-black h-4 px-1.5 border-none">LIVE</Badge>
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground">
                                {start.format('D MMM')} — {end.format('D MMM')}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {(isHappeningNow || start.diff(now, 'days') >= 0) && (
                              <div className={cn(
                                "flex flex-col items-center justify-center min-w-[55px] h-12 rounded-lg border px-2",
                                isHappeningNow ? "bg-primary/20 border-primary/30" : "bg-background border-border"
                              )}>
                                <span className={cn(
                                  "text-xl font-black leading-none",
                                  isHappeningNow ? "text-primary" : "text-foreground"
                                )}>
                                  {isHappeningNow ? (daysLeft === 0 ? '!' : daysLeft) : (start.diff(now, 'days') === 0 ? '!' : start.diff(now, 'days'))}
                                </span>
                                <span className="text-[7px] font-black uppercase tracking-tighter text-muted-foreground">
                                  {isHappeningNow ? (daysLeft === 0 ? 'LAST DAY' : 'DAYS LEFT') : (start.diff(now, 'days') === 0 ? 'STARTS TODAY' : 'DAYS LEFT')}
                                </span>
                              </div>
                            )}
                            {event.tags && event.tags.map(tag => (
                              <Badge key={tag} variant="outline" className={cn(
                                "text-[8px] px-1.5 h-4 border-none",
                                gameTags.includes(tag) ? "bg-primary/20 text-primary" : "bg-card text-muted-foreground"
                              )}>
                                {tag}
                              </Badge>
                            ))}
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => window.open(event.url, '_blank')}>
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <div className="p-6 bg-card/20 border-t border-border">
            <Button className="w-full bg-primary hover:bg-primary/90 font-bold" onClick={() => setShowFullCalendar(false)}>
              Close Calendar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
